import { useTranslation } from 'react-i18next'
import { useScrollReveal } from '@/hooks/use-scroll-reveal'
import Container from '@/components/ui/container'

interface CardItem {
  icon: string
  title: string
  description: string
}

interface FeatureItem {
  icon: string
  title: string
  description: string
}

interface PhaseProps {
  i18nKey: string
  layout: 'left' | 'right'
}

export default function Phase({ i18nKey, layout }: PhaseProps) {
  const { t } = useTranslation()
  const { ref, visible } = useScrollReveal()

  const cards = t(`${i18nKey}.cards`, { returnObjects: true, defaultValue: '' }) as
    | CardItem[]
    | string
  const features = t(`${i18nKey}.features`, { returnObjects: true, defaultValue: '' }) as
    | FeatureItem[]
    | string

  const hasCards = Array.isArray(cards)
  const hasFeatures = Array.isArray(features)

  const metric = t(`${i18nKey}.metric`, { defaultValue: '' })
  const metricLabel = t(`${i18nKey}.metricLabel`, { defaultValue: '' })
  const quote = t(`${i18nKey}.quote`, { defaultValue: '' })

  return (
    <section ref={ref} className="py-12 md:py-16 lg:py-24 relative">
      <Container>
        <div
          className={`transition-all duration-700 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div
            className={`flex flex-col lg:flex-row gap-8 md:gap-12 lg:gap-16 items-start ${layout === 'right' ? 'lg:flex-row-reverse' : ''}`}
          >
            <div className="lg:w-5/12 shrink-0">
              <span className="text-primary font-body text-label-md uppercase tracking-[0.4em] mb-4 block">
                {t(`${i18nKey}.label`)}
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter uppercase mb-6">
                {t(`${i18nKey}.title`)}
              </h2>
              {quote && (
                <p className="text-primary/80 text-lg italic border-l-2 border-primary/30 pl-5 mb-6">
                  {quote}
                </p>
              )}
              <p className="text-on-surface-variant text-lg leading-relaxed">
                {t(`${i18nKey}.description`)}
              </p>

              {metric && (
                <div className="mt-10 flex items-baseline gap-3">
                  <span className="text-primary font-display text-4xl md:text-5xl lg:text-6xl font-extrabold">
                    {metric}
                  </span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-[0.2em]">
                    {metricLabel}
                  </span>
                </div>
              )}
            </div>

            <div className="lg:w-7/12">
              {hasCards && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cards.map((card, i) => (
                    <div
                      key={i}
                      className={`p-5 bg-surface-container-low border border-outline-variant/15 hover:border-primary/30 transition-all duration-300 ${
                        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                      }`}
                      style={{ transitionDelay: visible ? `${(i + 2) * 100}ms` : '0ms' }}
                    >
                      <span className="material-symbols-outlined text-primary text-2xl mb-3 block">
                        {card.icon}
                      </span>
                      <h3 className="text-on-surface text-sm font-bold mb-1">{card.title}</h3>
                      <p className="text-on-surface-variant text-xs leading-relaxed">
                        {card.description}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {hasFeatures && (
                <div className="space-y-6">
                  {features.map((feature, i) => (
                    <div
                      key={i}
                      className={`flex items-start gap-4 transition-all duration-500 ${
                        visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                      }`}
                      style={{ transitionDelay: visible ? `${(i + 2) * 150}ms` : '0ms' }}
                    >
                      <span className="material-symbols-outlined text-primary text-2xl mt-0.5 shrink-0">
                        {feature.icon}
                      </span>
                      <div>
                        <h3 className="text-on-surface font-bold text-sm mb-1">{feature.title}</h3>
                        <p className="text-on-surface-variant text-sm leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
